import axios from 'axios';
import Auth from './Auth';
import { handleErrors, objToQueryString } from 'services/utils';

const isDevMode = process.env.NODE_ENV === 'development';
const registeredSources = {};
const pathPrefix = isDevMode ? '/api' : '/api';
const APIdomain = isDevMode ? 'http://localhost:3000' : window.location.origin;
let authSubscribed = false;

export function prepareUrl(baseUrl, noPrefix) {
  let url = baseUrl;
  if (url.indexOf('http') !== 0) {
    if (url[0] !== '/') {
      url = `/${url}`;
    }
    url = `${APIdomain}${noPrefix ? '' : pathPrefix}${url}`;
  }
  return url;
}


function processUrl(urlTpl, params) {
  let url = urlTpl;
  const placeholders = url.match(/{[^:]*:[^}]+}/g);
  const requestParams = {...params};
  if (!placeholders) {
    return [url, requestParams];
  }
  placeholders.forEach((placeholder) => {
    const paramName = placeholder.match(/^{(.*):([^}]*)}$/);
    if (requestParams[paramName[2]]) {
      url = url.replace(placeholder, paramName[1] + requestParams[paramName[2]]);
      delete (requestParams[paramName[2]]);
    } else {
      url = url.replace(placeholder, '');
    }
  });
  return [url, requestParams];
}

function isValueAFile(entry) {
  const value = entry[1];
  const result = value && typeof(value) === 'object' && value.constructor === File;
  return result;
}

function addValueToFormData (formdata, key, value) {
  if (typeof value === 'object' && value.constructor !== File) {
    Object.keys(value).forEach((subKey) => {
      const paramName = `${key}[${subKey}]`;
      addValueToFormData(formdata, paramName, value[subKey]);
    });
  } else {
    formdata.append(key, value);
  }
  return formdata;
}

function updateAuthHeaders() {
  axios.defaults.headers.common['Authorization'] = Auth.isReady() ? (Auth.authToken || '') : '';
}

function updateAuthKey(response) {
  if (response.headers && response.headers['access-token']) {
    const accessToken = response.headers['access-token'];
    const expiry      = response.headers['expiry'];
    if (accessToken && expiry) {
      Auth.updateAuthToken(accessToken, expiry);
    }
  }
}


class DataSource {
  constructor(url, wrapParamsWith) {
    this.url = url;
    this.requestParams = {};
    this.items = [];
    this.paging = {};
    this.hooks = {
      dataRequested: [],
      dataReceived: [],
    };
    this.wrapParamsWith = wrapParamsWith || null;
    this.headers = {};
    this.authRequired = true;
  }


  digestParams(params) {
    let digested;
    if (Object.entries(params).some(isValueAFile)) {
      // params contains file data, send it as form
      this.headers = { 'Content-Type': 'multipart/form-data'};
      digested = new FormData();
      Object.keys(params).forEach((key) => {
        let paramName = this.wrapParamsWith ? `${this.wrapParamsWith}[${key}]` : key;
        addValueToFormData(digested, paramName, params[key]);
      });
      return digested;
    } else {
      if (this.wrapParamsWith) {
        return {[this.wrapParamsWith]: params};
      }
      return params;
    }
  }

  rejectNoAuth() {
    if (this.authRequired && !Auth.isAuthenticated) {
      return Promise.reject({
        response: {status: 401}
      });
    }
  }

  query(params, customUrl) {
    let requestUrl = customUrl ? prepareUrl(customUrl) : this.url;
    const [url, requestParams] = processUrl(requestUrl, params);
    let promise = new Promise((resolve, reject) => {
      const ds = this.rejectNoAuth() || axios.get(url, {params: requestParams});
      (ds).then((response) => {
        updateAuthKey(response);
        let data = response.data;
        if (data.items) {
          this.items = response.data.items || [];
          this.paging = response.data.paging || {};
        } else {
          this.items = response.data || [];
          this.paging = null;
        }
        resolve(this);
      }).catch(error => {
        if (error.response.status === 401) {
          Auth.checkToken();
          console.log('Not Authorized');
        } else {
          handleErrors(error);
        }
        reject(error);
      });
    })
    return promise;
  }

  get(id, params={}) {
    let [url, requestParams] = processUrl(this.url, {...params, id });
    if (requestParams) {
      url += '?' + objToQueryString(requestParams);
    }
    const result = axios.get(url).then(response => {
      updateAuthKey(response);
      return response.data;
    });
    return result;
  }

  post(params) {
    const [url, requestParams] = processUrl(this.url, params);
    const digestedParams = this.digestParams(requestParams, this);
    return axios.post(url, digestedParams, this.headers).then(response => {
      updateAuthKey(response);
      return response.data;
    });
  }

  update(params) {
    const [url, requestParams] = processUrl(this.url, params);
    const digestedParams = this.digestParams(requestParams, this);
    return axios.put(url, digestedParams, this.headers).then(response => {
      updateAuthKey(response);
      return response.data;
    });
  }

  delete(obj) {
    const [url] = processUrl(this.url, obj);
    return axios.delete(url).then(response => {
      updateAuthKey(response);
      return obj;
    });
  }

}

const dataSource = {
  getSource(name, baseUrl, wrapParamsWith) {
    if (!Object.prototype.hasOwnProperty.call(registeredSources, name)) {
      registeredSources[name] = new DataSource(prepareUrl(baseUrl), wrapParamsWith);
    }
    if (!authSubscribed) {
      updateAuthHeaders();
      Auth.subscribe(updateAuthHeaders);
      authSubscribed = true;
    }
    return registeredSources[name];
  },
};
export default dataSource;